import { ProportionalityRuleEnum } from '@/Network/Types/Requests/ScheduleRequests'
import type { ImportableScheduleItem } from '@/Network/Types/Responses/ScheduleUnitResponses'
import { hoursToMinutes } from './timeConverter'

// Prazo limite para solicitar troca do plantão
export const doctorHandoverAuthorizationMinutesOptions = [
  { title: 'Até 48 horas antes do início do plantão', value: hoursToMinutes(48) },
  { title: 'Até 24 horas antes do início do plantão', value: hoursToMinutes(24) },
  { title: 'Até 12 horas antes do início do plantão', value: hoursToMinutes(12) },
  { title: 'Até 6 horas antes do início do plantão', value: hoursToMinutes(6) },
  { title: 'Até o início do plantão', value: hoursToMinutes(0) }
]

export const proportionalityRuleOptions = [
  {
    value: ProportionalityRuleEnum.PROPORTIONAL_WITH_LIMIT,
    title: 'Proporcional com limite',
    description: 'Desconto por atraso e/ou saída antecipada, sem pagamento adicional.'
  },
  {
    value: ProportionalityRuleEnum.PROPORTIONAL_WITHOUT_LIMIT,
    title: 'Proporcional sem limite',
    description:
      'Desconto por atraso e/ou saída antecipada. Com pagamento adicional mediante autorização da supervisão.'
  },
  {
    value: ProportionalityRuleEnum.INTEGRAL,
    title: 'Integral',
    description:
      'Pagamento integral do plantão, independente de atraso e/ou saída antecipada. Sem pagamento adicional.'
  }
]

export type ImportableUnitNode = {
  id: number
  name: string
  schedules: { id: number; name: string }[]
}

export type ImportableCompanyNode = {
  id: number
  name: string
  units: ImportableUnitNode[]
}

/**
 * Agrupa a lista plana de `GET /schedules/importable-configurations` na árvore
 * Empresa → Unidade → Escala que o modal de importação renderiza.
 *
 * O backend já devolve as linhas ordenadas por empresa → unidade → order → id, e
 * este agrupamento preserva essa ordem de chegada. Por isso é seguro chamar a função
 * sobre o acumulado de várias páginas: cada página nova apenas estende os nós
 * existentes ou acrescenta nós no fim.
 */
export function groupImportableSchedules(items: ImportableScheduleItem[]): ImportableCompanyNode[] {
  const companies: ImportableCompanyNode[] = []
  const companyIndex = new Map<number, ImportableCompanyNode>()
  const unitIndex = new Map<string, ImportableUnitNode>()

  items.forEach((item) => {
    let company = companyIndex.get(item.company.id)
    if (!company) {
      company = { id: item.company.id, name: item.company.name, units: [] }
      companyIndex.set(company.id, company)
      companies.push(company)
    }

    // A chave da unidade inclui a empresa: unidades de empresas distintas nunca
    // colidem, mesmo que o backend repita ids entre tenants.
    const unitKey = `${item.company.id}:${item.healthUnit.id}`
    let unit = unitIndex.get(unitKey)
    if (!unit) {
      unit = { id: item.healthUnit.id, name: item.healthUnit.name, schedules: [] }
      unitIndex.set(unitKey, unit)
      company.units.push(unit)
    }

    unit.schedules.push({ id: item.id, name: item.name })
  })

  return companies
}

export function getProportionalityRuleTitle(
  ruleValue: ProportionalityRuleEnum | string | null | undefined
): string {
  if (!ruleValue) {
    return 'Não definida'
  }
  const foundPropRule = proportionalityRuleOptions.find(
    (propRuleOption) => propRuleOption.value === ruleValue
  )
  return foundPropRule ? foundPropRule.title : 'Não definida'
}
